/**
 * ADR-0016 — query inventory movements for history UI.
 */

import { listStockMovements, DEFAULT_STORE_ID } from '../movements';

/**
 * @param {object} parsed
 * @param {{ productId?: string|number, lotId?: string|number, location?: string, storeId?: string, movementType?: string, limit?: number, includeArchive?: boolean }} [filters]
 */
export function queryInventoryMovementHistory(parsed, filters = {}) {
  const list = listStockMovements(parsed, {
    productId: filters.productId,
    lotId: filters.lotId,
    location: filters.location,
    storeId: filters.storeId,
    movementType: filters.movementType,
    includeArchive: filters.includeArchive !== false
  });

  const sorted = list.slice().sort((a, b) => {
    const sa = Number(a.ledgerSequence) || 0;
    const sb = Number(b.ledgerSequence) || 0;
    if (sa !== sb) return sb - sa; // newest first
    const ta = new Date(a.occurredAt || a.createdAt || a.recordedAt || 0).getTime();
    const tb = new Date(b.occurredAt || b.createdAt || b.recordedAt || 0).getTime();
    return tb - ta;
  });

  const limit = filters.limit != null ? Number(filters.limit) : 100;
  const rows = limit > 0 ? sorted.slice(0, limit) : sorted;

  return {
    storeId: filters.storeId || DEFAULT_STORE_ID,
    total: sorted.length,
    rows: rows.map((m) => ({
      id: m.id || m.movementId,
      ledgerSequence: m.ledgerSequence,
      movementType: m.movementType,
      quantity: Number(m.quantity) || 0,
      productId: m.productId,
      lotId: m.lotId,
      location: m.location || 'Principal',
      storeId: m.storeId || DEFAULT_STORE_ID,
      unitCost: m.unitCost,
      referenceId: m.referenceId,
      referenceType: m.referenceType,
      correlationId: m.correlationId,
      note: m.note,
      sessionId: m.sessionId,
      actorId: m.actorId,
      qtyBefore: m.qtyBefore,
      qtyAfter: m.qtyAfter,
      occurredAt: m.occurredAt || m.createdAt || m.recordedAt
    }))
  };
}

/**
 * Load history for business (blob + archive).
 */
export async function loadInventoryMovementHistory(businessId, filters = {}) {
  const { normalizeBusinessStorageId, readBestBusinessMergedPayload } = await import(
    '../../../utils/sharedCore'
  );
  const bid = normalizeBusinessStorageId(businessId);
  if (!bid) return { total: 0, rows: [], error: 'business_id_missing' };

  let parsed = readBestBusinessMergedPayload(bid)?.parsed || {};

  // Prefer sync cache for lots only; movements usually from blob / entity store
  try {
    const { readStockMovementsSyncForBusiness } = await import(
      '../../../utils/stockMovementsReadSource'
    );
    const cached = readStockMovementsSyncForBusiness?.(bid);
    if (Array.isArray(cached) && cached.length) {
      parsed = { ...parsed, stockMovements: cached };
    }
  } catch {
    /* optional */
  }

  return queryInventoryMovementHistory(parsed, filters);
}
